import React from 'react';
import { CustomField } from '../types';
import { Calendar, Mail, Phone } from 'lucide-react';

interface DynamicFormFieldsProps {
  fields: CustomField[];
  values: Record<string, any>;
  errors: Record<string, string>;
  onChange: (fieldId: string, value: any) => void;
}

const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({
  fields,
  values,
  errors,
  onChange,
}) => {
  if (!fields || fields.length === 0) {
    return null;
  }

  // Sort fields by order
  const sortedFields = [...fields].sort((a, b) => a.order - b.order);

  const renderField = (field: CustomField) => {
    const value = values[field.id] || '';
    const error = errors[field.id];
    const inputId = `custom_field_${field.id}`;

    const baseInputClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 ${
      error ? 'border-red-300' : 'border-gray-300'
    }`;

    const labelClasses = 'block text-sm font-medium text-gray-700 mb-1';

    switch (field.type) {
      case 'text':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              id={inputId}
              value={value}
              onChange={(e) => onChange(field.id, e.target.value)}
              className={baseInputClasses}
              placeholder={field.label}
              required={field.required}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              id={inputId}
              value={value}
              onChange={(e) => onChange(field.id, e.target.value)}
              className={baseInputClasses}
              placeholder={field.label}
              required={field.required}
              rows={3}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="number"
              id={inputId}
              value={value}
              onChange={(e) => onChange(field.id, e.target.value)}
              className={baseInputClasses}
              placeholder={field.label}
              required={field.required}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'email':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                id={inputId}
                value={value}
                onChange={(e) => onChange(field.id, e.target.value)}
                className={`pl-10 ${baseInputClasses}`}
                placeholder={field.label}
                required={field.required}
              />
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'phone':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                id={inputId}
                value={value}
                onChange={(e) => onChange(field.id, e.target.value)}
                className={`pl-10 ${baseInputClasses}`}
                placeholder={field.label}
                required={field.required}
              />
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id={inputId}
                value={value}
                onChange={(e) => {
                  // Format as DD/MM/YYYY
                  let val = e.target.value.replace(/\D/g, '');
                  if (val.length >= 2) {
                    val = val.substring(0, 2) + '/' + val.substring(2);
                  }
                  if (val.length >= 5) {
                    val = val.substring(0, 5) + '/' + val.substring(5, 9);
                  }
                  onChange(field.id, val);
                }}
                className={`pl-10 ${baseInputClasses}`}
                placeholder="DD/MM/AAAA"
                required={field.required}
                maxLength={10}
              />
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id}>
            <label htmlFor={inputId} className={labelClasses}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <select
              id={inputId}
              value={value}
              onChange={(e) => onChange(field.id, e.target.value)}
              className={baseInputClasses}
              required={field.required}
            >
              <option value="">Selecione...</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="flex items-center">
            <input
              type="checkbox"
              id={inputId}
              checked={value === true}
              onChange={(e) => onChange(field.id, e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              required={field.required}
            />
            <label htmlFor={inputId} className="ml-2 block text-sm text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            {error && <p className="mt-1 text-sm text-red-600 ml-6">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {sortedFields.map(field => renderField(field))}
    </div>
  );
};

export default DynamicFormFields;
